import React, { useState, useEffect } from "react";
import TextInput from "../fields/TextInput";
import { MdTune } from "react-icons/md";
import ModalShell from "../modals/ModalShell";
import Button from "../common/Button";

interface MassAssignSetsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    rpe?: number | null;
    rir?: number | null;
    rest_time_seconds?: number | null;
  }) => void;
  trainingDayName: string;
  exerciseCount: number;
  loading?: boolean;
  error?: string | null;
}

const MassAssignSetsModal: React.FC<MassAssignSetsModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  trainingDayName,
  exerciseCount,
  loading = false,
  error = null,
}) => {
  const [rir, setRir] = useState<string>("");
  const [rpe, setRpe] = useState<string>("");
  const [restSeconds, setRestSeconds] = useState<string>("");

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setRir("");
      setRpe("");
      setRestSeconds("");
    }
  }, [isOpen]);

  // Sync RIR and RPE (they are inversely related: RPE = 10 - RIR)
  // This matches the existing behavior in SortableExerciseItem
  const handleRirChange = (value: string) => {
    setRir(value);
    if (value === "") {
      setRpe("");
    } else {
      const numVal = parseInt(value);
      if (!isNaN(numVal)) {
        const clampedRir = Math.min(10, Math.max(0, numVal));
        setRpe((10 - clampedRir).toString());
      }
    }
  };

  const handleRpeChange = (value: string) => {
    setRpe(value);
    if (value === "") {
      setRir("");
    } else {
      const numVal = parseInt(value);
      if (!isNaN(numVal)) {
        const clampedRpe = Math.min(10, Math.max(0, numVal));
        setRir((10 - clampedRpe).toString());
      }
    }
  };

  const handleSubmit = () => {
    const data: {
      rpe?: number | null;
      rir?: number | null;
      rest_time_seconds?: number | null;
    } = {};

    // Only include fields that have values
    if (rir !== "") {
      const rirVal = parseInt(rir);
      if (!isNaN(rirVal) && rirVal >= 0 && rirVal <= 10) {
        data.rir = rirVal;
      }
    }

    if (rpe !== "") {
      const rpeVal = parseInt(rpe);
      if (!isNaN(rpeVal) && rpeVal >= 0 && rpeVal <= 10) {
        data.rpe = rpeVal;
      }
    }

    if (restSeconds !== "") {
      const restVal = parseInt(restSeconds);
      if (!isNaN(restVal) && restVal >= 0 && restVal <= 600) {
        data.rest_time_seconds = restVal;
      }
    }

    // Only submit if at least one field has a value
    if (Object.keys(data).length > 0) {
      onSubmit(data);
    }
  };

  const hasValidInput = () => {
    const hasRir =
      rir !== "" &&
      !isNaN(parseInt(rir)) &&
      parseInt(rir) >= 0 &&
      parseInt(rir) <= 10;
    const hasRpe =
      rpe !== "" &&
      !isNaN(parseInt(rpe)) &&
      parseInt(rpe) >= 0 &&
      parseInt(rpe) <= 10;
    const hasRest =
      restSeconds !== "" &&
      !isNaN(parseInt(restSeconds)) &&
      parseInt(restSeconds) >= 0 &&
      parseInt(restSeconds) <= 600;
    return hasRir || hasRpe || hasRest;
  };

  if (!isOpen) return null;

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title="Mass Assign Values"
      icon={<MdTune className="h-6 w-6 text-biafit-primary" />}
      headerVariant="compact"
      size="md"
      layout="fixed"
      closeDisabled={loading}
      message={error ? { text: error, type: "error" } : null}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={loading}
            loadingLabel="Applying..."
            disabled={!hasValidInput()}
          >
            Apply to All
          </Button>
        </>
      }
    >
      {/* Content */}
      <div className="p-6">
        <p className="mb-4 text-gray-600 dark:text-gray-300">
          Set values for all{" "}
          <span className="font-semibold text-navy-700 dark:text-white">
            {exerciseCount} exercise{exerciseCount !== 1 ? "s" : ""}
          </span>{" "}
          in{" "}
          <span className="font-semibold text-navy-700 dark:text-white">
            {trainingDayName}
          </span>
        </p>

        <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
          Only fill in the values you want to update. Empty fields will not be
          changed.
        </p>

        {/* Form Fields */}
        <div className="space-y-4">
          {/* RIR */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              RIR
            </label>
            <TextInput
              type="number"
              min="0"
              max="10"
              step="1"
              value={rir}
              onKeyDown={(e) => {
                if (
                  e.key === "-" ||
                  e.key === "+" ||
                  e.key === "e" ||
                  e.key === "E" ||
                  e.key === "."
                ) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => handleRirChange(e.target.value)}
            />
          </div>

          {/* RPE */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              RPE
            </label>
            <TextInput
              type="number"
              min="1"
              max="10"
              step="1"
              value={rpe}
              onKeyDown={(e) => {
                if (
                  e.key === "-" ||
                  e.key === "+" ||
                  e.key === "e" ||
                  e.key === "E" ||
                  e.key === "."
                ) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => handleRpeChange(e.target.value)}
            />
          </div>

          {/* Rest Time */}
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Rest Time (seconds)
            </label>
            <TextInput
              type="number"
              min="0"
              max="600"
              step="5"
              value={restSeconds}
              onKeyDown={(e) => {
                if (
                  e.key === "-" ||
                  e.key === "+" ||
                  e.key === "e" ||
                  e.key === "E" ||
                  e.key === "."
                ) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => setRestSeconds(e.target.value)}
            />
          </div>
        </div>

        {/* Preview */}
        {hasValidInput() && (
          <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-900/20">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              This will update{" "}
              {[
                rir !== "" && `RIR to ${rir}`,
                rpe !== "" && `RPE to ${rpe}`,
                restSeconds !== "" && `Rest to ${restSeconds}s`,
              ]
                .filter(Boolean)
                .join(", ")}{" "}
              for all exercises.
            </p>
          </div>
        )}
      </div>
    </ModalShell>
  );
};

export default MassAssignSetsModal;
